<template>
  <div class="attachments">
    <radio-list
      v-show="!schema.attachments.$items.length && showRadio"
      label="Czy chcesz dodać załączniki?"
      :options="options"
      @selected="optionSelected"
    />

    <div v-if="!showRadio || showAttachments">
      <table class="table table-bordered table-striped w-100 dataTable dtr-inline">
        <thead>
          <tr role="row">
            <th v-if="showDownloadJoined" width="20px">
              <w-checkbox class="mb-0" :model="$schema.checkAll" @input="checkAll" />
            </th>

            <th width="5%">Lp.</th>

            <th>{{ attachmentColumnName }}</th>

            <th width="350px">
              <span class="sr-only">Akcje</span>
            </th>
          </tr>
        </thead>

        <tbody>
          <attachment-list-item
            v-for="(item, index) in schema.attachments.$items"
            :key="'att_' + index"
            :index="index"
            :attachment="item.$item"
            :is-first="index === 0"
            :is-last="index === schema.attachments.$items.length - 1"
            :disable-order="editIndex !== undefined || disableOrder"
            :editable="editable"
            :use-index-as-order="useIndexAsOrder"
            :show-checkbox="showDownloadJoined"
            @cancel="cancelEdit"
            @decrementOrder="swap(index, false)"
            @edit="edit(item.$item, index)"
            @editDate="showDocumentValuesModal(item.$item)"
            @incrementOrder="swap(index, true)"
            @removeAttachment="showConfirmModal({ index })"
            @generate-again="generateAgain(item.$item)"
            @save="save"
          />
        </tbody>
      </table>

      <check-permission
        v-if="editable && showAddButton"
        permissions="ATTACHMENT.ADD"
        :entity-type="entityType"
        :entity-id="entityId"
      >
        <attachment-upload
          class="d-inline mr-2"
          :add-files-text="addFilesText"
          :additional-metadata="additionalMetadata"
          :entity-id="entityId"
          :entity-type="entityType"
          :model="schema.attachments"
          :attachment-type="attachmentType"
          :default-description="defaultDescription"
          @attachmentAdded="addAttachmentToList"
        />
      </check-permission>

      <check-permission
        v-if="mdokEnabled && editable && showAddFromMdokButton"
        permissions="DOCUMENT.MDOK"
        :entity-type="entityType"
        :entity-id="entityId"
      >
        <attachment-add-mdok
          class="d-inline"
          :entity-id="entityId"
          :entity-type="entityType"
          :attachment-type="attachmentType"
          :additional-metadata="additionalMetadata"
          @attachmentAdded="addAttachmentToList"
        />
      </check-permission>

      <post-form
        v-if="showDownloadJoined"
        class="float-right"
        text="Pobierz zaznaczone"
        icon="fas fa-download mr-2"
        :url="renderJoinedUrl"
        :fields="renderJoinedParams"
        :disabled="!checkedIds.length"
      />

      <slot name="footer" />
    </div>

    <confirm-modal
      :show="confirmModalVisible"
      :submit-data="submitData"
      :text="confirmModalText"
      @submit="handleConfirmModalSubmit"
      @close="hideConfirmModal"
    />
  </div>
</template>

<script>
import { components } from '@witkac/bootstrap4-components';
import { mapState } from 'vuex';

import RadioList from '@/components/formComponents/RadioList';
import PostForm from '@/components/postForm/PostForm';
import { Errors } from '@/enums/errors.enum';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { Successes } from '@/enums/successes.enum';
import { confirmModalMixin } from '@/mixins/confirmModal';
import { formMixin } from '@/mixins/form';
import { modalFormMixin } from '@/mixins/modalForm';
import { notificationsMixin } from '@/mixins/notifications';
import schema from '@/schemas/checkAll.schema';
import { config } from '@/utils/config';
import { random } from '@/utils/number';
import AttachmentListItem from './attachmentList/AttachmentListItem';
import AttachmentUpload from './AttachmentUpload';
import AttachmentAddMdok from './AttachmentAddMdok';
import { serverError } from '@/utils/serverError';

const documentValuesMessages = {
  getError: Errors.DOCUMENT_VALUE_CANNOT_GET,
  added: Successes.DOCUMENT_VALUE_ADDED,
  addError: Errors.DOCUMENT_VALUE_ADD_ERROR,
  updated: Successes.DOCUMENT_VALUE_UPDATED,
  updateError: Errors.DOCUMENT_VALUE_UPDATE_ERROR
};

// @vuese
// Krok ze szczegółowymi danymi procedury
// @group Attachment
export default {
  components: {
    ...components,
    AttachmentUpload,
    AttachmentAddMdok,
    AttachmentListItem,
    PostForm,
    RadioList
  },
  mixins: [modalFormMixin('documentValue', documentValuesMessages), notificationsMixin, confirmModalMixin, formMixin],
  schema,
  props: {
    // Show radio
    showRadio: { type: Boolean, required: false, default: true },
    // Schema
    schema: { type: Object, required: true },
    // Typ encji
    entityType: { type: String, required: false },
    // Id encji
    entityId: { type: String, required: false },
    // Dodatkowe metadata do załącznika
    additionalMetadata: { type: Object, required: false, default: undefined },
    // Tekst do wyświetlenia w komponencie do wgrywania plików
    addFilesText: { type: String, required: false, default: 'Dodaj pliki' },
    // Typ załącznika
    attachmentType: { type: String, required: false },
    // Czy ma być możliwość edytowania
    editable: { type: Boolean, default: true },
    // Błędy
    errors: { type: Array, default: () => [] },
    // Przycisk dodawania
    showAddButton: { type: Boolean, required: false, default: true },
    // Schowaj przyciski zmiany kolejności
    disableOrder: { type: Boolean, required: false, default: false },
    // Domyślny opis załącznika
    defaultDescription: { type: String, required: false, default: undefined },
    // Wymuś poprawne numerowanie - blokuje możliwość zmiany kolejności
    useIndexAsOrder: { type: Boolean, required: false, default: false },
    // Pokaż przycisk dodawania z MDOK
    showAddFromMdokButton: { type: Boolean, required: false, default: false },
    // Pokaż przycisk pobierania zaznaczonych
    showDownloadJoined: { type: Boolean, default: false }
  },
  data() {
    return {
      attachmentToEditValues: {},
      options: [
        { value: false, text: 'Nie, nie chcę wgrać załączników.', id: 'index_' + random() },
        { value: true, text: 'Tak, chcę wgrać załączniki.', id: 'index_' + random() }
      ],
      previousModel: undefined,
      visibleAttachments: undefined,
      mdokEnabled: config.mdokEnabled,
      renderJoinedUrl: config.renderURL + '/pdf/attachments'
    };
  },
  computed: {
    ...mapState({
      attachmentColumnName(state) {
        return this.$route.name === 'procedureDocumentation'
          ? state.setting.settings[PublicSettings.PROCEDURE_ATTACHMENT_COLUMN_NAME]?.value || 'Załącznik'
          : 'Załącznik';
      }
    }),
    editIndex() {
      const index = this.schema.attachments.$items.findIndex(item => item.$item.$model.edit);
      return index > -1 ? index : undefined;
    },
    showAttachments() {
      return this.visibleAttachments || !!this.schema.attachments.$items.length;
    },
    confirmModalText() {
      return 'Kliknij, aby potwierdzić usunięcie załącznika.';
    },
    checkedIds() {
      return this.schema.attachments.$model.filter(attachment => attachment.checked).map(attachment => attachment.id);
    },
    renderJoinedParams() {
      return [{ name: 'attachmentIds', value: this.checkedIds }];
    }
  },
  watch: {
    visibleAttachments(value) {
      if (value) {
        this.validateSchema();
      } else {
        this.emitErrors([]);
      }
    }
  },
  mounted() {
    this.validateSchema();
  },
  methods: {
    // @vuese
    // Tworzenie załącznika
    addAttachmentToList(attachment) {
      this.schema.attachments.$addRow(attachment);
      this.$emit('attachmentAdded', attachment);
      this.validateSchema();
    },
    // @vuese
    // Włączenie edycji pliku
    edit(item, index) {
      this.previousModel = JSON.parse(JSON.stringify({ ...item.$model }));
      this.cancelEdit();
      item.$model.edit = true;
    },
    // @vuese
    // Anulowanie edycji
    cancelEdit() {
      if (this.editIndex !== undefined) {
        this.schema.attachments.$items[this.editIndex].$item.$setData(this.previousModel);
      }
    },
    // @vuese
    // Zapisywanie pojedyńczego załącznika
    save(item) {
      this.saveAttachment(item)
        .then(id => {
          this.schema.attachments.$items[this.editIndex].$item.edit.$setData(false);
          return this.notifySuccess(Successes.ATTACHMENT_SAVED);
        })
        .catch(err => this.notifyError(Errors.ATTACHMENT_CANNOT_SAVE));
    },
    // @vuese
    // Zapisanie pojedyńczego załącznika
    saveAttachment(item) {
      if (this.schema.attachments.$validateAll().length) {
        this.notifyError(Errors.FORM_CORRECT_ERRORS);
        return Promise.reject();
      }
      return this.$store.dispatch(item.$model.entityType + '/attachment/update', item.$getData());
    },
    // @vuese
    // Usunięcie załącznika o danym indeksie w tabeli
    removeAttachment(index) {
      const attachmentId = this.schema.attachments.$items[index].$item.$model.id || undefined;
      if (attachmentId) {
        this.$store
          .dispatch(this.schema.attachments.$items[index].$item.$model.entityType + '/attachment/remove', attachmentId)
          .then(() => this.removeAttachmentFromList(index));
        this.$emit('attachmentRemoved', attachmentId);
      } else {
        this.removeAttachmentFromList(index);
      }
    },
    // @vuese
    // usunięcie załącznika z listy
    removeAttachmentFromList(index) {
      this.schema.attachments.$items[index].$item.$removeRow();
      this.changeOrder();
      this.notifySuccess(Successes.ATTACHMENT_REMOVED);
      this.validateSchema();
    },
    // @vuese
    // Zmiana kolejności i zapis do bazy
    changeOrder() {
      Promise.all(
        this.schema.attachments.$items.map((item, i) => {
          item.$item.$value.order = i + 1;
          return this.saveAttachment(item.$item);
        })
      );
    },
    // @vuese
    // Usunięcie wszystkich załączników z schemy
    clearSchemaList() {
      for (let i = this.schema.attachments.$items.length - 1; i >= 0; i--) {
        this.schema.attachments.$items[i].$item.$removeRow();
      }
    },
    // @vuese
    // Zamiana wierszy w tabeli
    swap(index, up) {
      if (up) {
        this.schema.attachments.$items[index].$item.$moveUp();
      } else {
        this.schema.attachments.$items[index].$item.$moveDown();
      }
      const otherIndex = up ? index - 1 : index + 1;

      this.schema.attachments.$items[index].$item.$model.order = index + 1;
      this.schema.attachments.$items[otherIndex].$item.$model.order = otherIndex + 1;

      Promise.all([
        this.saveAttachment(this.schema.attachments.$items[index].$item),
        this.saveAttachment(this.schema.attachments.$items[otherIndex].$item)
      ])
        .then(() => this.notifySuccess(Successes.TABLE_SWAPPED))
        .catch(error => this.notifyError(Errors.TABLE_CANNOT_SWAP));
    },
    // @vuese
    // Wybranie opcji czy użytkownik chce wgrać załączniki
    optionSelected(val) {
      this.visibleAttachments = val;
    },
    // @vuese
    // Emitowanie tabelii z błędami
    emitErrors(val) {
      this.$emit('errors', val);
    },
    // @vuese
    // Walidowanie schemy
    validateSchema() {
      this.emitErrors(this.schema.$validateAll());
    },
    // @vuese
    // Wywołuje odpowiednią funkcje dla modala potwierdzenia
    handleConfirmModalSubmit(submitData) {
      this.removeAttachment(submitData.index);
    },
    // @vuese
    // Zaznacza lub odznacza wszystkie załączniki
    checkAll() {
      const checked = this.$schema.checkAll.$getData();
      this.schema.attachments.$items.forEach(item => item.$item.checked.$setData(checked));
    },

    // @vuese
    // Generuje załącznik ponownie
    generateAgain(attachment) {
      return this.$store
        .dispatch('attachment/generateAgain', { id: attachment.$model.id })
        .then(() => this.notifySuccess(Successes.ATTACHMENT_GENERATED_AGAIN))
        .catch(err => this.notifyError(serverError(err)));
    }
  }
};
</script>

<style>
.attachments .table .mb-0 {
  margin-bottom: 0 !important;
}
</style>
